import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, Plus, Eye, Trash2, Shield } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import CreatePoliceVerificationModal from './CreatePoliceVerificationModal';
import ViewPoliceVerificationModal from './ViewPoliceVerificationModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PoliceVerifications = () => {
  const { token } = useAuth();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchVerifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/police-verification`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifications(response.data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast({
        title: "Error",
        description: "Failed to fetch police verifications",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleViewVerification = (verification) => {
    setSelectedVerification(verification);
    setShowViewModal(true);
  };

  const handleDeleteVerification = (verification) => {
    setSelectedVerification(verification);
    setShowDeleteModal(true);
  };

  const confirmDeleteVerification = async () => {
    if (!selectedVerification) return;

    setDeleteLoading(true);

    try {
      await axios.delete(`${API}/police-verification/${selectedVerification.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Verification Deleted",
        description: "Police verification has been deleted successfully"
      });

      fetchVerifications();
      setShowDeleteModal(false);
      setSelectedVerification(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete verification",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const stats = {
    total: verifications.length,
    registered: verifications.filter(v => v.status === 'Registered').length,
    completed: verifications.filter(v => v.status === 'Completed').length
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading verifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Police Verifications</h1>
          <p className="text-gray-600 mt-1">Manage tenant police verification requests</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-red-600 hover:bg-red-700"
        >
          <Plus size={20} className="mr-2" />
          Create Verification
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-l-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Verifications</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Shield className="text-red-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Registered</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.registered}</p>
            </div>
            <FileText className="text-blue-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Completed</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.completed}</p>
            </div>
            <FileText className="text-green-600" size={32} />
          </div>
        </Card>
      </div>

      {/* Verifications List */}
      {verifications.length === 0 ? (
        <Card className="p-12 text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Verifications Found</h3>
          <p className="text-gray-600 mb-4">Create your first police verification request</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus size={20} className="mr-2" />
            Create Verification
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {verifications.map((verification) => (
            <Card key={verification.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-lg">
                      <Shield size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{verification.tenant_name}</h3>
                      <p className="text-sm text-gray-600">Room {verification.room_number}</p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                          {verification.status}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                          {verification.service_request_no}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-gray-600">
                      <span className="font-semibold">Owner: </span>
                      <span>{verification.owner_name}</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Mobile: </span>
                      <span>{verification.tenant_mobile}</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">District: </span>
                      <span>{verification.district}</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Office: </span>
                      <span>{verification.office_name}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleViewVerification(verification)}
                    size="sm"
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    <Eye size={16} className="mr-1" />
                    View
                  </Button>
                  <Button
                    onClick={() => handleDeleteVerification(verification)}
                    size="sm"
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <CreatePoliceVerificationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchVerifications}
        token={token}
      />

      {selectedVerification && (
        <ViewPoliceVerificationModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedVerification(null);
          }}
          verification={selectedVerification}
        />
      )}

      {selectedVerification && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedVerification(null);
          }}
          onConfirm={confirmDeleteVerification}
          title="Delete Police Verification"
          message={`Are you sure you want to delete the police verification for ${selectedVerification.tenant_name}? This action cannot be undone.`}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default PoliceVerifications;